import { isAxiosError } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';

/** Per-model spend slice of a {@link UsageStatus}. `cost` is a USD float. */
export interface ModelSpend {
  model: string;
  cost: number;
  call_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

/**
 * By-model spend rollup returned by both the per-conversation
 * (`/planner/{uuid}/cost`) and per-hunt (`/hunt/{uuid}/cost`) endpoints. Cost
 * is projected at read time from the raw per-model token usage stored on the
 * conversation(s) — `total` is the grand sum, `by_model` the breakdown.
 *
 * `total.total_tokens === 0` (with an empty `by_model`) means no usage was
 * ever recorded — e.g. an old conversation created before usage capture, or
 * one that hasn't run a turn. Render an "unknown" empty state in that case
 * (see {@link isUsageUnknown}) rather than a misleading `$0.00`.
 */
export interface UsageStatus {
  total: ModelSpend;
  by_model: ModelSpend[] | null;
  currency: string;
}

/** True when a rollup carries no recorded token usage (cost is unknown, not
 *  zero) — the empty-state signal for old / never-run conversations. */
export const isUsageUnknown = (u?: UsageStatus | null): boolean =>
  u == null || !u.total || u.total.total_tokens === 0;

/**
 * Reads the on-demand USD cost for a single AI Assistant conversation.
 * Customer-facing (`EntitlementConversationAI`). Returns `null` when the
 * conversation UUID is not found in the caller's partition (backend 404);
 * a `200` with no recorded usage renders an "unknown" empty state; only
 * `null` (404) hides the chip.
 */
export const useConversationCost = (conversationId?: string) => {
  const axios = useAxios();

  const queryKey = useGetUserKey(
    getQueryKey.getConversationCost(conversationId ?? '')
  );

  return useQuery<UsageStatus | null>({
    queryKey,
    enabled: !!conversationId,
    queryFn: async () => {
      try {
        const { data } = await axios.get<UsageStatus>(
          `/planner/${encodeURIComponent(conversationId ?? '')}/cost`
        );
        return data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load conversation cost',
  });
};
